import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Permission } from '@frontend/authorization';
import { ClassType, EntityType, Locale } from '@frontend/common';
import { HorizontalButtonGroupButton } from '@frontend/components/common';
import {
    FilterOptions,
    HeaderFilter,
    InsaneFilter,
    ObjectColumnsProps,
    ObjectListActionButtons,
    ObjectListLayout,
    ObjectListLoadingTable,
    useObjectListHeightMode,
    useObjectListLayout
} from '@frontend/components/list';
import { ConfirmationModal } from '@frontend/components/modal';
import { TablePlaceholder } from '@frontend/elements';
import { ICONS } from '@frontend/icons';
import { useActiveModal } from '@frontend/modal';
import { Report } from '@frontend/report/types';
import {
    DELETED_ROW_GRACE_PERIOD_MS,
    DefaultListValues,
    ResolveFunction,
    TableAutoRefreshMode,
    markRowPendingDelete,
    removeRow,
    resetSelect
} from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { ReactiveRowElement } from '../element/reactive-row-element.component';
import { ReactiveTable, ReactiveTableProps } from '../table/reactive-table';
import { ObjectListRefreshBanner } from './object-list-refresh-banner.component';
import useReactiveObjectList from './reactive-object-list.controller';
import { resolveTableHeightProps } from './resolve-table-height-props';

export interface ReactiveObjectListProps<T extends { id: string }> {
    listStoreIdCallback?: (id: string) => void;
    label?: React.ReactNode;
    fetch: (arg?: ApiQueryParams<DefaultQueryParams | string | number>) => Promise<PaginatedResponse<T>>;
    dispatch: ThunkDispatch<any, any, Action>;
    eventListener?: EntityType;
    /**
     * How MQTT create events refresh this list: `on` | `delayed` | `manual`.
     * Defaults to the table-view preference when Table options are available, otherwise `delayed`.
     * Local creates via `notifyEntityCreated` always refresh regardless of this setting.
     */
    autoRefreshMode?: TableAutoRefreshMode | null;
    id?: string;
    /**
     * @default false
     * @description When true with a stable `id`, reuse that id in the Redux list cache across remounts.
     */
    useStaticListId?: boolean;
    columns: Omit<ObjectColumnsProps<T>, 'dispatch'>;
    multiSelectOptions?: MultiSelectOptions<T>;
    onSelectElement?: (object: T) => void;
    sortingColumns?: string[];
    /**
     * @description Columns where the sort direction should be reversed when sent to the backend.
     * This is useful for computed values where ascending/descending needs to be inverted.
     */
    reverseSortingColumns?: string[];
    create?: {
        label?: React.ReactNode;
        onCreate: () => void;
        icon?: HorizontalButtonGroupButton['icon'];
        requiredPermissions?: Permission[];
        overwriteCreateLabel?: React.ReactNode;
    };
    includeDeleted?: {
        requiredPermissions: Permission[];
    };
    type: EntityType;
    /**
     * @description Overrides the resolver that rows use to refetch themselves. Only needed when
     * several resolvers share `type`, as the group member types do.
     */
    resolveFunction?: ResolveFunction;
    filter?: FilterOptions;
    activeFilter?: {
        searchKeys?: string[];
        filterKeys?: string[];
        extrtaProps?: Map<string, object>;
        displayName?: Map<string, React.ReactNode>;
        queryKeyMap?: Map<string, string>;
        export?: {
            overwriteExport?: () => void;
            modalId?: string;
            report?: Report;
            data?: { [key: string]: any };
            dataKeys?: string[];
        };
    };
    selectedObjects?: (obj: T[]) => void;
    defaultListValues?: DefaultListValues;

    cardClassName?: string;
    cardHeaderClassName?: string;
    cardBodyClassName?: string;
    rowClassName?: string;
    customDivclassName?: string;
    customLabelClassName?: string;
    customButtons?: HorizontalButtonGroupButton[];
    customHeaderComponent?: React.ReactNode;
    tableProps?: { [K in keyof ReactiveTableProps]?: ReactiveTableProps[K] };
    hideColumns?: string[];

    showCount?: boolean;
    locale?: Locale;
}

export interface MultiSelectOptions<T> {
    delete?: {
        enabled: (obj: T[]) => boolean;
        delete: (ids: string[]) => Promise<void>;
        label?: React.ReactNode;
        icon?: HorizontalButtonGroupButton['icon'];
        parent?: React.ReactNode;
        requiredPermissions?: Permission[];
    };
    custom?: HorizontalButtonGroupButton[];
}

/**
 * Live list that leaves its rows alone unless they change.
 *
 * MQTT creations follow an on / delayed / manual refresh policy (default delayed, or the user’s
 * table-view preference). Local creates always refetch. Updates and deletions are handled by each
 * row through its own entity subscription: an update patches that one row, a deletion greys the row
 * out and drops it a moment later.
 *
 * Pass `autoRefreshMode` to override the preference/default for MQTT creates.
 */
export const ReactiveObjectList = <T extends { id: string }>(props: ReactiveObjectListProps<T>) => {
    const viewProps = useReactiveObjectList<T>(props);
    const location = useLocation();
    const { modalId } = useActiveModal();
    const intl = useIntl();
    const listLayout = useObjectListLayout();
    const heightMode = useObjectListHeightMode();
    const isInitialLoading = viewProps.isLoading && !viewProps.isUpdating;
    const usesAdminLayout = listLayout === 'admin' || location.pathname.includes('/admin');
    const rawTableViewId = props.tableProps?.tableViewId ?? props.id;
    const shrinkUnlessExplicit = resolveTableHeightProps(heightMode, props.tableProps);
    const tableOptionsEnabled = !!rawTableViewId && isMainPlatformTableOptionsRoute(location.pathname, listLayout, modalId);
    const tableViewId = tableOptionsEnabled ? rawTableViewId : undefined;
    const [isTableViewCustomized, setIsTableViewCustomized] = useState(false);

    useEffect(() => {
        setIsTableViewCustomized(false);
    }, [tableViewId]);

    const tableOptionsLabel = intl.formatMessage({
        id: 'ObjectList.TableOptionsButton',
        description: 'Button label for opening the table column visibility and ordering options',
        defaultMessage: 'Table options'
    });
    const customizedTableOptionsLabel = intl.formatMessage({
        id: 'ObjectList.TableOptionsButton.Customized',
        description: 'Accessible label for the table options button when the user has customized the visible columns or their order',
        defaultMessage: 'Table options (customized)'
    });
    const accessibleTableOptionsLabel = isTableViewCustomized ? customizedTableOptionsLabel : tableOptionsLabel;
    const tableOptionsControlClassName = isTableViewCustomized
        ? 'bg-brand-light outline-brand-accent hover:bg-brand-light inline-flex size-11 items-center justify-center rounded-lg outline-1 outline-offset-[-1px] hover:cursor-pointer'
        : 'hover:bg-background-default outline-border-default bg-background-surface inline-flex size-11 items-center justify-center rounded-lg outline-1 outline-offset-[-1px] hover:cursor-pointer';
    const tableOptionsButtonId = tableViewId ? `${tableViewId}-table-options-button` : 'ObjectList.TableOptionsButton';
    const tableOptionsControl = tableOptionsEnabled ? (
        <button
            type='button'
            id={tableOptionsButtonId}
            aria-label={accessibleTableOptionsLabel}
            title={accessibleTableOptionsLabel}
            className={tableOptionsControlClassName}
            onClick={() => viewProps.setOptionsModalOpen(true)}>
            <ICONS.BUTTON.SETTINGS className={isTableViewCustomized ? 'text-brand-primary size-5' : 'text-brand-muted size-5'} />
        </button>
    ) : null;
    const tableOptionsButton: HorizontalButtonGroupButton = {
        hide: !tableOptionsEnabled,
        onClick: () => viewProps.setOptionsModalOpen(true),
        icon: ICONS.BUTTON.SETTINGS,
        id: tableOptionsButtonId,
        ariaLabel: accessibleTableOptionsLabel,
        title: accessibleTableOptionsLabel,
        className: isTableViewCustomized
            ? 'btn bg-brand-light text-brand-primary outline outline-1 outline-brand-accent hover:bg-brand-light'
            : 'btn btn-brand-secondary'
    };
    const customButtons = [...(props.customButtons ?? []), tableOptionsButton];

    return (
        <div
            id={props.id}
            className={'bg-background-surface border-border-default rounded-xl border ' + (props.cardClassName !== undefined ? props.cardClassName : 'w-full')}>
            {usesAdminLayout ? (
                <div
                    id='admin-table-header'
                    className={props.cardHeaderClassName !== undefined ? props.cardHeaderClassName : 'inline-flex w-full items-center justify-between p-4'}>
                    <div className='flex w-full flex-col items-start md:flex-row md:content-start md:items-center md:justify-between'>
                        <div className='size- order-1 mb-2 inline-flex items-center justify-start gap-3 md:mb-0 md:w-1/3'>
                            {props.filter && (
                                <HeaderFilter<T>
                                    dispatch={props.dispatch}
                                    filter={props.filter}
                                    {...viewProps}
                                    setOptionsModalOpen={tableOptionsEnabled ? viewProps.setOptionsModalOpen : undefined}
                                />
                            )}
                        </div>
                        <h4 className='text-brand-primary order-1 m-0 ms-2 mb-2 text-2xl font-semibold md:order-2 md:mb-0 md:w-1/3 md:text-center'>
                            {props.label}
                        </h4>
                        {props.customHeaderComponent && props.customHeaderComponent}
                        <div className='order-2 mb-2 md:order-3 md:mb-0 md:w-1/3'>
                            <ObjectListActionButtons<T>
                                {...props}
                                {...viewProps}
                                customButtons={customButtons}
                                onMultiDelete={() =>
                                    viewProps.openModal(viewProps.multiDeleteModalId, () => (
                                        <ConfirmationModal
                                            handleClose={() => viewProps.closeModal(viewProps.multiDeleteModalId)}
                                            message={
                                                props.multiSelectOptions?.delete?.parent
                                                    ? parentMultiDeleteMessage(
                                                          viewProps.selectedObjects.length,
                                                          props.type,
                                                          props.multiSelectOptions.delete.parent
                                                      )
                                                    : listMultiDeleteMessage(viewProps.selectedObjects.length, props.type)
                                            }
                                            onConfirm={() => {
                                                props
                                                    .multiSelectOptions!.delete!.delete(viewProps.selectedObjects.map((o) => o.id))
                                                    .then(() => {
                                                        const ids = viewProps.selectedObjects.map((o) => o.id);
                                                        ids.forEach((id) => props.dispatch(markRowPendingDelete({ listId: viewProps.listId, id })));
                                                        setTimeout(
                                                            () => ids.forEach((id) => props.dispatch(removeRow({ listId: viewProps.listId, id }))),
                                                            DELETED_ROW_GRACE_PERIOD_MS
                                                        );
                                                        if (viewProps.listId) {
                                                            props.dispatch(resetSelect({ listId: viewProps.listId }));
                                                        }
                                                        viewProps.changeSelectedObjects([]);
                                                    })
                                                    .then(() => viewProps.closeModal(viewProps.multiDeleteModalId));
                                            }}
                                            severity={ClassType.DANGER}
                                            show={true}
                                            cascadeWarning
                                        />
                                    ))
                                }
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className='flex flex-col'>
                    {(props.label || props.customHeaderComponent || (tableOptionsEnabled && !props.activeFilter)) && (
                        <div className='inline-flex items-center justify-between self-stretch'>
                            <div
                                className={
                                    props.customLabelClassName
                                        ? props.customLabelClassName
                                        : 'inline-flex items-start justify-start gap-2.5 self-stretch px-4 pt-4'
                                }>
                                <div className='text-foreground-default justify-start text-xl font-semibold'>{props.label}</div>
                            </div>
                            <div className='inline-flex items-center justify-end gap-3 px-4 pt-4'>
                                {props.customHeaderComponent && props.customHeaderComponent}
                                {!props.activeFilter && tableOptionsControl}
                            </div>
                        </div>
                    )}
                    {props.activeFilter && (
                        <InsaneFilter
                            dispatch={props.dispatch}
                            listId={viewProps.listId}
                            {...props.activeFilter}
                            trailingActions={tableOptionsControl}
                        />
                    )}
                </div>
            )}
            <ObjectListRefreshBanner
                count={viewProps.pendingCreateCount}
                hasStaleRows={viewProps.hasStaleRows}
                onRefresh={viewProps.onManualRefresh}
                onDismiss={viewProps.onDismissRefresh}
            />
            <div
                aria-busy={viewProps.isUpdating}
                className='pointer-events-none h-0.5 w-full overflow-hidden'>
                {viewProps.isUpdating && <div className='bg-brand-structure h-full w-full animate-pulse' />}
            </div>
            <div className={'card-body mx-0 mb-0 px-0 pt-0 pb-0' + (props.cardBodyClassName ? ` ${props.cardBodyClassName}` : '')}>
                {viewProps.error ? (
                    <div className='flex flex-col items-center justify-center p-8 text-center'>
                        <p className='text-brand-muted text-lg'>
                            <FormattedMessage
                                id='ObjectList.error.data-unavailable'
                                defaultMessage='Data is unavailable'
                            />
                        </p>
                    </div>
                ) : isInitialLoading ? (
                    <ObjectListLoadingTable columns={viewProps.columns.length} />
                ) : viewProps.data ? (
                    <ReactiveTable
                        dispatch={props.dispatch}
                        listId={viewProps.listId}
                        entityType={props.type}
                        resolveFunction={props.resolveFunction}
                        onRowFallback={viewProps.onRowFallback}
                        useStaticListId={props.useStaticListId}
                        customDivclassName={props.customDivclassName}
                        key={props.id}
                        id={props.id ? props.id + '-table' : undefined}
                        columns={viewProps.columns as ColumnDef<any>[]}
                        RowElement={ReactiveRowElement}
                        data={viewProps.data}
                        isUpdating={viewProps.isUpdating}
                        selectEnabled={!!props.multiSelectOptions}
                        onSelectElement={props.onSelectElement}
                        rowClassName={props.rowClassName}
                        sortingColumns={props.sortingColumns}
                        reverseSortingColumns={props.reverseSortingColumns}
                        hideColumns={props.hideColumns}
                        showCount={props.showCount}
                        {...shrinkUnlessExplicit}
                        {...props.tableProps}
                        optionsModalOpen={viewProps.optionsModalOpen}
                        setOptionsModalOpen={viewProps.setOptionsModalOpen}
                        tableViewId={tableViewId}
                        tableOptionsEnabled={tableOptionsEnabled}
                        onTableViewCustomizationChange={setIsTableViewCustomized}
                        onAutoRefreshModeChange={viewProps.onAutoRefreshModeChange}
                    />
                ) : (
                    <TablePlaceholder />
                )}
            </div>
        </div>
    );
};

const isMainPlatformTableOptionsRoute = (pathname: string, listLayout: ObjectListLayout, activeModalId: string): boolean => {
    if (listLayout === 'admin' || activeModalId) return false;

    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] === 'admin') return false;

    return segments.length > 0 && segments.length <= 2;
};

const listMultiDeleteMessage = (count: number, type: EntityType) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message'
            defaultMessage='Are you sure you want to remove {count} {type}s?'
            values={{ count, type: type.replace(/_/g, ' ') }}
        />
    );
};

const parentMultiDeleteMessage = (count: number, type: EntityType, parent: React.ReactNode) => {
    return (
        <FormattedMessage
            id='ObjectList.multi-delete-modal.message_with_parent'
            defaultMessage='Are you sure you want to remove {count} {type}s from this {parent}?'
            values={{ count, type: type.replace(/_/g, ' '), parent }}
        />
    );
};
